<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { UiButton, UiText } from '@shopexperts/shared';
import SuccessCheckmarkIcon from '../../../assets/icons/success-chekmark.svg';
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";

interface Props {
  hasSelectedExpert: boolean;
  expertName?: string;
  disableAutoRedirect?: boolean;
  title?: string;
  description?: string;
}

const props = defineProps<Props>();

const router = useRouter();
const { isEnabled } = useFeatureFlags();
const countdown = ref(10);
let countdownInterval: ReturnType<typeof setInterval> | null = null;

// Start countdown on mount (only if auto-redirect is enabled)
onMounted(() => {
  if (props.disableAutoRedirect) return;
  
  countdownInterval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      navigateToDashboard();
    }
  }, 1000);
});

// Cleanup interval on unmount
onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});

function navigateToDashboard() {
  router.push(isEnabled('enable_v2_projects') ? '/client/projects' : '/client/leads');
}
</script>

<template>
  <div class="flex flex-col items-center justify-center gap-6 py-12 text-center min-h-[500px]">
    <!-- Success Icon -->
    <div class="w-20 h-20">
      <SuccessCheckmarkIcon class="w-full h-full" />
    </div>

    <!-- Success Message -->
    <div class="flex flex-col gap-2 max-w-xl">
      <UiText as="h3" variant="h3" class="font-semibold text-se-gray-900">
        {{ title || "Your project request has been submitted!" }}
      </UiText>
      
      <UiText 
        v-if="description"
        as="p" 
        variant="p" 
        class="text-se-gray-600"
      >
        {{ description }}
      </UiText>

      <UiText 
        v-else-if="hasSelectedExpert" 
        as="p" 
        variant="p" 
        class="text-se-gray-600"
      >
        Your request has been sent to <span class="font-semibold">{{ expertName }}</span>. 
        They will review your project details and get back to you shortly. You can track 
        the status of your request in your dashboard.
      </UiText>
      
      <UiText 
        v-else 
        as="p" 
        variant="p" 
        class="text-se-gray-600"
      >
        We're now matching you with the perfect expert for your project. You'll receive 
        notifications when experts show interest. You can track the status and review 
        proposals in your dashboard.
      </UiText>
    </div>

    <!-- Countdown Message (only show when auto-redirect is enabled) -->
    <UiText v-if="!disableAutoRedirect" as="p" variant="p-small" class="text-se-gray-500">
      Redirecting to dashboard in {{ countdown }} second{{ countdown !== 1 ? 's' : '' }}...
    </UiText>

    <!-- Action Button (only show when auto-redirect is enabled) -->
    <UiButton 
      v-if="!disableAutoRedirect"
      variant="primary" 
      label="Go to Dashboard"
      @click="navigateToDashboard"
      class="min-w-[200px]"
    />

    <slot name="actions" />
  </div>
</template>

